import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { trackCustomEvent } from 'app/utils/statistics/clientStatistics';
import { titlePlayExpandSeason, titlePlayShowAllUpcoming } from 'app/utils/statistics/customEvents';
import {
    Section,
    Header,
    SectionExpandArrow,
    SectionHeaderLink,
    ShowMoreButton,
    ShowMoreIcon,
} from './AssociatedContent.styles';
import { ButtonType } from '../../../../ui-components/PlayButton/PlayButton';

type AssociatedContentSectionProps = {
    name: string;
    id: string;
    children: JSX.Element[];
    defaultExpanded: boolean;
    selectedVideoId: string;
    max: number;
    defaultShowAll?: boolean;
};

const AssociatedContentSection = ({
    name,
    id,
    children,
    defaultExpanded,
    selectedVideoId,
    max,
    defaultShowAll = false,
}: AssociatedContentSectionProps) => {
    const childrenArray = React.Children.toArray(children);

    const [expanded, setExpanded] = useState(defaultExpanded);
    const [showAll, setShowAll] = useState(defaultShowAll || childrenArray.length <= max);
    const router = useRouter();

    useEffect(() => {
        setExpanded(defaultExpanded);
    }, [defaultExpanded]);

    const { tab, ...queryWithoutTab } = router.query; // eslint-disable-line no-unused-vars
    const queryParamsVideo = expanded ? queryWithoutTab : { ...router.query, tab: id };

    const path = router.pathname;

    const handleExpand = () => {
        trackCustomEvent(titlePlayExpandSeason(name, selectedVideoId));
        setExpanded(!expanded);
    };

    const handleShowAll = () => {
        trackCustomEvent(titlePlayShowAllUpcoming(selectedVideoId));
        setShowAll(true);
    };

    return (
        <Section data-helix-type="list">
            <Header as="h2" expanded={expanded}>
                <Link
                    href={{
                        pathname: path,
                        query: { ...queryParamsVideo },
                    }}
                    passHref
                    scroll={false}
                >
                    <SectionHeaderLink
                        onMouseUp={(e) => {
                            e.currentTarget.blur();
                        }}
                        onClick={handleExpand}
                        aria-expanded={expanded}
                        data-rt="associated-content-accordion-header"
                    >
                        <SectionExpandArrow expanded={expanded} />
                        {name}
                    </SectionHeaderLink>
                </Link>
            </Header>
            {expanded &&
                (showAll ? (
                    <div id={id}>{children}</div>
                ) : (
                    <>
                        {childrenArray.slice(0, max)}
                        <ShowMoreButton
                            buttonType={ButtonType.BUTTON}
                            isCentered
                            onClick={handleShowAll}
                            aria-label="Visa fler avsnitt"
                        >
                            <ShowMoreIcon aria-hidden />
                            Visa fler
                        </ShowMoreButton>
                    </>
                ))}
        </Section>
    );
};

export default AssociatedContentSection;
